import { type ReactElement, useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AutoScrollActivator,
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { formatISO, startOfMonth } from 'date-fns'
import { SquareArrowLeft } from 'lucide-react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { toast } from 'sonner'
import { linkPaymentsToCost } from '@/features/indirect-costs/api/indirectCostsLinksService'
import { getInvoice, linkPaymentsToInvoice } from '@/features/invoices/api/invoicesService'
import { updatePaymentLinkingRequirement } from '@/features/payments/api/linkingRequirementService'
import { ConnectBankModal } from '@/features/bank-connections'
import { CreateInvoiceDialog } from '@/features/invoices/components/CreateInvoiceDialog'
import { useBulkInvoiceQueue, BULK_INVOICE_SOFT_LIMIT } from '@/features/invoices/hooks/useBulkInvoiceQueue'
import { EditInvoiceDialog } from '@/features/invoices/components/EditInvoiceDialog'
import type { Invoice } from '@/features/invoices/schemas/invoicesSchema'
import { CostsColumn } from '../components/CostsColumn'
import { InvoicesColumn } from '../components/InvoicesColumn'
import { PaymentsColumn } from '../components/PaymentsColumn'
import { SearchLinkToggle, type SearchLinkState } from '../components/SearchLinkToggle'
import { ViewportArrow } from '../components/ViewportArrow'

type ViewportPosition = 0 | 1 | 2 | 3 | 4

interface CostsKanbanPageProps {
  initialPosition?: ViewportPosition
}

interface EditInvoiceDialogState {
  open: boolean
  invoice: Invoice | null
}

const EASE_TRANSITION = { duration: 0.35, ease: [0.4, 0, 0.2, 1] } as const

export function CostsKanbanPage({
  initialPosition = 0,
}: CostsKanbanPageProps): ReactElement {
  const { t } = useTranslation('features-indirect-costs')
  const queryClient = useQueryClient()

  const [viewportPosition, setViewportPosition] = useState<ViewportPosition>(initialPosition)

  const now = new Date()
  const [costsYear, setCostsYear] = useState(now.getFullYear())
  const [costsMonthIndex, setCostsMonthIndex] = useState(now.getMonth())
  const [paymentsYear, setPaymentsYear] = useState(now.getFullYear())
  const [paymentsMonthIndex, setPaymentsMonthIndex] = useState(now.getMonth())
  const [invoicesYear, setInvoicesYear] = useState(now.getFullYear())
  const [invoicesMonthIndex, setInvoicesMonthIndex] = useState(now.getMonth())

  const [paymentsSearchQuery, setPaymentsSearchQuery] = useState('')
  const [invoicesSearchQuery, setInvoicesSearchQuery] = useState('')
  const [linkState, setLinkState] = useState<SearchLinkState>(0)

  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const [connectBankOpen, setConnectBankOpen] = useState(false)
  const invoiceQueue = useBulkInvoiceQueue()
  const [editInvoiceDialog, setEditInvoiceDialog] = useState<EditInvoiceDialogState>({
    open: false,
    invoice: null,
  })
  const [recentlyLinkedCost, setRecentlyLinkedCost] = useState<{
    paymentId: string
    costUuid: string
  } | null>(null)
  const [recentlyLinkedInvoice, setRecentlyLinkedInvoice] = useState<{
    paymentId: string
    invoiceUuid: string
  } | null>(null)
  const linkedFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isDualColumn = viewportPosition === 1 || viewportPosition === 3
  const canDragPayments = viewportPosition === 1
  const canLinkPaymentsToCosts = viewportPosition === 1
  const canLinkInvoicesToPayments = viewportPosition === 3
  const canDragInvoices = viewportPosition === 3

  const goLeft = useCallback(() => {
    setViewportPosition(prev => (prev > 0 ? (prev - 1) as ViewportPosition : prev))
  }, [])
  const goRight = useCallback(() => {
    setViewportPosition(prev => (prev < 4 ? (prev + 1) as ViewportPosition : prev))
  }, [])

  const handlePaymentsSearchChange = (query: string): void => {
    setPaymentsSearchQuery(query)
    if (linkState >= 1) setInvoicesSearchQuery(query)
  }

  const handleInvoicesSearchChange = (query: string): void => {
    setInvoicesSearchQuery(query)
    if (linkState >= 1) setPaymentsSearchQuery(query)
  }

  const handlePaymentsMonthChange = (y: number, m: number): void => {
    setPaymentsYear(y)
    setPaymentsMonthIndex(m)
    if (linkState === 2) {
      setInvoicesYear(y)
      setInvoicesMonthIndex(m)
    }
  }

  const handleInvoicesMonthChange = (y: number, m: number): void => {
    setInvoicesYear(y)
    setInvoicesMonthIndex(m)
    if (linkState === 2) {
      setPaymentsYear(y)
      setPaymentsMonthIndex(m)
    }
  }

  const handleLinkStateChange = (next: SearchLinkState): void => {
    if (next >= 1 && linkState < 1) {
      // Sync search queries when first linking: prefer the side that has content.
      if (paymentsSearchQuery && !invoicesSearchQuery) {
        setInvoicesSearchQuery(paymentsSearchQuery)
      } else if (invoicesSearchQuery && !paymentsSearchQuery) {
        setPaymentsSearchQuery(invoicesSearchQuery)
      } else if (paymentsSearchQuery !== invoicesSearchQuery) {
        setInvoicesSearchQuery(paymentsSearchQuery)
      }
    }
    if (next === 2 && linkState < 2) {
      setInvoicesYear(paymentsYear)
      setInvoicesMonthIndex(paymentsMonthIndex)
    }
    setLinkState(next)
  }

  useEffect(() => {
    const handleKey = (e: KeyboardEvent): void => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return
      }
      if (e.key === 'ArrowRight') goRight()
      else if (e.key === 'ArrowLeft') goLeft()
      else if (e.key === 'Escape' && activeDragId) setActiveDragId(null)
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [goLeft, goRight, activeDragId])

  const startLinkedFeedback = useCallback(() => {
    if (linkedFeedbackTimerRef.current !== null) {
      clearTimeout(linkedFeedbackTimerRef.current)
    }
    linkedFeedbackTimerRef.current = setTimeout(() => {
      setRecentlyLinkedCost(null)
      setRecentlyLinkedInvoice(null)
      linkedFeedbackTimerRef.current = null
    }, 2000)
  }, [])

  const linkCostMutation = useMutation({
    mutationFn: (args: { costUuid: string; paymentId: string }) =>
      linkPaymentsToCost(args.costUuid, { paymentUuids: [args.paymentId] }),
    onSuccess: (_data, variables) => {
      setRecentlyLinkedCost({
        paymentId: variables.paymentId,
        costUuid: variables.costUuid,
      })
      setRecentlyLinkedInvoice(null)
      startLinkedFeedback()
      void queryClient.invalidateQueries({ queryKey: ['indirect-costs'] })
      void queryClient.invalidateQueries({ queryKey: ['payments'] })
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t('toasts.linkFailed'))
    },
  })

  const linkInvoiceMutation = useMutation({
    mutationFn: (args: { invoiceUuid: string; paymentId: string }) =>
      linkPaymentsToInvoice(args.invoiceUuid, { paymentUuids: [args.paymentId] }),
    onSuccess: (_data, variables) => {
      setRecentlyLinkedInvoice({
        paymentId: variables.paymentId,
        invoiceUuid: variables.invoiceUuid,
      })
      setRecentlyLinkedCost(null)
      startLinkedFeedback()
      void queryClient.invalidateQueries({ queryKey: ['payments'] })
      void queryClient.invalidateQueries({ queryKey: ['invoices'] })
      void queryClient.invalidateQueries({ queryKey: ['invoices', 'links'] })
      void queryClient.invalidateQueries({ queryKey: ['payments', 'linked-invoices'] })
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t('toasts.linkFailed'))
    },
  })

  const costNotNeededMutation = useMutation({
    mutationFn: (paymentId: string) =>
      updatePaymentLinkingRequirement(paymentId, 'NOT_NEEDED'),
    onSuccess: (_data, paymentId) => {
      setRecentlyLinkedCost({ paymentId, costUuid: '' })
      setRecentlyLinkedInvoice(null)
      startLinkedFeedback()
      void queryClient.invalidateQueries({ queryKey: ['payments'] })
      void queryClient.invalidateQueries({ queryKey: ['indirect-costs'] })
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t('toasts.linkFailed'))
    },
  })

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(String(event.active.id))
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null)
      const { active, over } = event
      if (!over) return

      const paymentData = active.data.current as { type: string; paymentId: string } | undefined
      const invoiceData = active.data.current as { type: string; invoiceUuid: string } | undefined
      const costData = over.data.current as { type: string; costUuid: string } | undefined
      const paymentTargetData = over.data.current as { type: string; paymentId: string } | undefined

      if (paymentData?.type === 'payment' && costData?.type === 'cost' && canLinkPaymentsToCosts) {
        linkCostMutation.mutate({
          costUuid: costData.costUuid,
          paymentId: paymentData.paymentId,
        })
        return
      }

      const overType = over.data.current as { type: string } | undefined
      if (paymentData?.type === 'payment' && overType?.type === 'cost-not-needed' && canLinkPaymentsToCosts) {
        costNotNeededMutation.mutate(paymentData.paymentId)
        return
      }

      if (
        invoiceData?.type === 'invoice' &&
        paymentTargetData?.type === 'payment-target' &&
        canLinkInvoicesToPayments
      ) {
        linkInvoiceMutation.mutate({
          invoiceUuid: invoiceData.invoiceUuid,
          paymentId: paymentTargetData.paymentId,
        })
      }
    },
    [canLinkPaymentsToCosts, canLinkInvoicesToPayments, linkCostMutation, linkInvoiceMutation, costNotNeededMutation],
  )

  const handleDragCancel = useCallback(() => {
    setActiveDragId(null)
  }, [])

  const handleEditInvoice = useCallback((invoice: Invoice): void => {
    setEditInvoiceDialog({ open: true, invoice })
  }, [])

  const handleEditInvoiceByUuid = useCallback(
    async (invoiceUuid: string): Promise<void> => {
      try {
        const invoice = await getInvoice(invoiceUuid)
        setEditInvoiceDialog({ open: true, invoice })
      } catch (err: unknown) {
        toast.error(err instanceof Error ? err.message : t('toasts.loadInvoiceFailed'))
      }
    },
    [t],
  )

  const handleEditInvoiceDialogChange = useCallback((open: boolean): void => {
    setEditInvoiceDialog(prev => ({ open, invoice: open ? prev.invoice : null }))
  }, [])

  const handleInvoiceFilesSelected = useCallback(
    (files: File[]): void => {
      if (files.length > BULK_INVOICE_SOFT_LIMIT) {
        const ok = window.confirm(
          `You're about to queue ${files.length} files. Continue?`,
        )
        if (!ok) return
      }
      void invoiceQueue.enqueue(files)
    },
    [invoiceQueue],
  )

  useEffect(() => {
    return () => {
      if (linkedFeedbackTimerRef.current !== null) {
        clearTimeout(linkedFeedbackTimerRef.current)
      }
    }
  }, [])

  const canAutoScroll = useCallback((element: Element): boolean => {
    return element instanceof HTMLElement && element.dataset.kanbanScrollArea === 'true'
  }, [])

  // Animate widths with a 5-stop viewport:
  // 0: costs, 1: costs+payments, 2: payments, 3: payments+invoices, 4: invoices.
  const costsFlex = viewportPosition <= 1 ? 1 : 0
  const paymentsFlex = viewportPosition >= 1 && viewportPosition <= 3 ? 1 : 0
  const invoicesFlex = viewportPosition >= 3 ? 1 : 0

  // Max-width for centering in single mode — animate it so there's no jump.
  // In dual mode, no effective constraint; in single mode, constrain to 672px.
  const costsMaxWidth = viewportPosition === 1 ? 2000 : 672
  const paymentsMaxWidth = isDualColumn ? 2000 : 672
  const invoicesMaxWidth = viewportPosition === 3 ? 2000 : 672

  const costsOpacity = viewportPosition <= 1 ? 1 : 0
  const paymentsOpacity = viewportPosition >= 1 && viewportPosition <= 3 ? 1 : 0
  const invoicesOpacity = viewportPosition >= 3 ? 1 : 0
  const defaultInvoiceSaleDate = formatISO(
    startOfMonth(new Date(invoicesYear, invoicesMonthIndex, 1)),
    { representation: 'date' },
  )

  return (
    <div>
      {/* Viewport container */}
      <div className="relative mx-auto max-w-screen-2xl overflow-x-clip px-14 pt-8">
        {viewportPosition < 4 && (
          <ViewportArrow direction="right" onClick={goRight} />
        )}
        {viewportPosition > 0 && (
          <ViewportArrow direction="left" onClick={goLeft} />
        )}

        {/* Search/month link toggle — only visible in payments+invoices view.
            Sits in the 48px gap between the two search bars; gap center is 24px
            right of the container midpoint because the collapsed costs column
            offsets the inner row leftward. */}
        {viewportPosition === 3 && (
          <div
            className="pointer-events-auto absolute z-20 -translate-x-1/2 -translate-y-1/2"
            style={{ top: '118px', left: 'calc(50% + 24px)' }}
          >
            <SearchLinkToggle state={linkState} onChange={handleLinkStateChange} />
          </div>
        )}

        {/* DndContext always mounted to avoid remount flicker */}
        <DndContext
          autoScroll={{
            activator: AutoScrollActivator.Pointer,
            threshold: { x: 0.2, y: 0.14 },
            acceleration: 18,
            canScroll: canAutoScroll,
          }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="flex w-full gap-12">
            {/* Costs column */}
            <motion.div
              initial={{ flex: costsFlex, opacity: costsOpacity }}
              animate={{ flex: costsFlex, opacity: costsOpacity }}
              transition={EASE_TRANSITION}
              className="overflow-hidden min-w-0"
            >
              <motion.div
                initial={{ maxWidth: costsMaxWidth }}
                animate={{ maxWidth: costsMaxWidth }}
                transition={EASE_TRANSITION}
                className="mx-auto"
              >
                <CostsColumn
                  year={costsYear}
                  monthIndex={costsMonthIndex}
                  onMonthChange={(y, m) => {
                    setCostsYear(y)
                    setCostsMonthIndex(m)
                  }}
                  isDualColumn={isDualColumn}
                  isDragActive={activeDragId !== null}
                  showCostNotNeededDropZone={canLinkPaymentsToCosts}
                  recentlyLinkedCostUuid={recentlyLinkedCost?.costUuid ?? null}
                />
              </motion.div>
            </motion.div>

            {/* Payments column */}
            <motion.div
              initial={{ flex: paymentsFlex, opacity: paymentsOpacity }}
              animate={{ flex: paymentsFlex, opacity: paymentsOpacity }}
              transition={EASE_TRANSITION}
              className="overflow-hidden min-w-0"
            >
              <motion.div
                initial={{ maxWidth: paymentsMaxWidth }}
                animate={{ maxWidth: paymentsMaxWidth }}
                transition={EASE_TRANSITION}
                className="mx-auto"
              >
                <PaymentsColumn
                  year={paymentsYear}
                  monthIndex={paymentsMonthIndex}
                  onMonthChange={handlePaymentsMonthChange}
                  searchQuery={paymentsSearchQuery}
                  onSearchChange={handlePaymentsSearchChange}
                  isDualColumn={isDualColumn}
                  canDragPayments={canDragPayments}
                  canDropInvoices={canLinkInvoicesToPayments}
                  showSatisfactionStatus={canLinkPaymentsToCosts}
                  showInvoiceStatus={canLinkInvoicesToPayments}
                  isDragActive={activeDragId !== null}
                  onEditInvoice={handleEditInvoiceByUuid}
                  onConnectBank={() => setConnectBankOpen(true)}
                  recentlyLinkedPaymentId={
                    recentlyLinkedCost?.paymentId ?? recentlyLinkedInvoice?.paymentId ?? null
                  }
                />
              </motion.div>
            </motion.div>

            {/* Invoices column */}
            <motion.div
              initial={{ flex: invoicesFlex, opacity: invoicesOpacity }}
              animate={{ flex: invoicesFlex, opacity: invoicesOpacity }}
              transition={EASE_TRANSITION}
              className="overflow-hidden min-w-0"
            >
              <motion.div
                initial={{ maxWidth: invoicesMaxWidth }}
                animate={{ maxWidth: invoicesMaxWidth }}
                transition={EASE_TRANSITION}
                className="mx-auto"
              >
                <InvoicesColumn
                  year={invoicesYear}
                  monthIndex={invoicesMonthIndex}
                  onMonthChange={handleInvoicesMonthChange}
                  searchQuery={invoicesSearchQuery}
                  onSearchChange={handleInvoicesSearchChange}
                  isDualColumn={isDualColumn}
                  canDragInvoices={canDragInvoices}
                  showSatisfactionStatus={canLinkInvoicesToPayments}
                  recentlyLinkedInvoiceUuid={recentlyLinkedInvoice?.invoiceUuid ?? null}
                  onEditInvoice={handleEditInvoice}
                  onUploadInvoiceFiles={handleInvoiceFilesSelected}
                />
              </motion.div>
            </motion.div>
          </div>

          <DragOverlay dropAnimation={null}>
            {activeDragId ? (
              <div className="rounded-xl border border-primary/40 bg-card p-4 shadow-xl opacity-90 max-w-[280px]">
                <p className="flex items-center gap-2 text-base text-foreground truncate">
                  <SquareArrowLeft className="size-5 shrink-0 text-foreground/90" />
                  <span className="truncate">
                    {canLinkInvoicesToPayments
                      ? t('kanban.dragInvoiceOntoPayment')
                      : t('kanban.dragPaymentOntoCost')}
                  </span>
                </p>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      <ConnectBankModal open={connectBankOpen} onOpenChange={setConnectBankOpen} />
      <CreateInvoiceDialog
        queue={invoiceQueue}
        defaultSaleDate={defaultInvoiceSaleDate}
        year={invoicesYear}
        monthIndex={invoicesMonthIndex}
        onNavigateToMonth={(year, monthIndex) => {
          setInvoicesYear(year)
          setInvoicesMonthIndex(monthIndex)
        }}
      />
      <EditInvoiceDialog
        open={editInvoiceDialog.open}
        onOpenChange={handleEditInvoiceDialogChange}
        invoice={editInvoiceDialog.invoice}
      />
    </div>
  )
}
